import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearCredentials, setCredentials } from "../../authSlice";

function BalanceSheet() {
  const { token } = useSelector((state) => state.auth);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [totalOpeningBalance, setTotalOpeningBalance] = useState(0);
  const [totalClosingBalance, setTotalClosingBalance] = useState(0);
  const [totalTodaysDebit, setTotalTodaysDebit] = useState(0);
  const [totalTodaysCredit, setTotalTodaysCredit] = useState(0);
  const [allAreas, setAllAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [btnDisable, setBtnDisable] = useState(false);
  const dispatch = useDispatch();
  const [tokenExpired, setTokenExpired] = useState(false);

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (/^[0-9-]*$/.test(value) && value.length <= 10) {
      const value = e.target.value; // always yyyy-mm-dd
      const date = new Date(value);
      const formatted = date.toLocaleDateString("en-GB"); // dd/mm/yyyy visually
      console.log("Display:", formatted);
      setSelectedDate(value);
    }
  };

  useEffect(() => {
    const fetchAreas = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getAllAreas`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setAllAreas(data || []);
        } else {
          throw new Error("Failed to fetch balance.");
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAreas();
  }, [dispatch, token]);

  useEffect(() => {
    const fetchLedgers = async () => {
      setLoading(true);
      setBtnDisable(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryDataByDateAndAreaData?limit=${recordsPerPage}?page=${currentPage}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
          }
          const data = await response.json();
          setLedgerEntries(data.histories);
          setTotalOpeningBalance(data.totals.totalOpeningBalance);
          setTotalClosingBalance(data.totals.totalClosingBalance);
          setTotalTodaysDebit(data.totals.totalTodaysDebit);
          setTotalTodaysCredit(data.totals.totalTodaysCredit);
          setTotalPage(data.pagination.totalPages);
        } else {
          throw new Error("Failed to fetch balance.");
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
        setBtnDisable(false);
      }
    };

    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }

    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
      return;
    } else {
      fetchLedgers();
    }
  }, [dispatch, token, tokenExpired, currentPage, recordsPerPage]);

  const filterLedgers = async () => {
    setLoading(true);
    const url =
      selectedArea && selectedDate
        ? `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryDataByDateAndAreaData?date=${selectedDate}&area=${selectedArea}`
        : selectedDate
        ? `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryDataByDateAndAreaData?date=${selectedDate}`
        : selectedArea
        ? `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryDataByDateAndAreaData?area=${selectedArea}`
        : `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryDataByDateAndAreaData`;

    try {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const data = await response.json();
        setLedgerEntries(data.histories);
        setTotalOpeningBalance(data.totals.totalOpeningBalance);
        setTotalClosingBalance(data.totals.totalClosingBalance);
        setTotalTodaysDebit(data.totals.totalTodaysDebit);
        setTotalTodaysCredit(data.totals.totalTodaysCredit);
      } else {
        throw new Error("Failed to fetch balance.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const printLedgers = async () => {
    setLoading(true);
    const url =
      selectedArea && selectedDate
        ? `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryByDateAndArea?date=${selectedDate}&area=${selectedArea}`
        : selectedDate
        ? `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryByDateAndArea?date=${selectedDate}`
        : selectedArea
        ? `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryByDateAndArea?area=${selectedArea}`
        : `${process.env.REACT_APP_API_URL}/sales/getLedgerHistoryByDateAndArea`;

    try {
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const blob = await response.blob();
        // Create a download URL for the blob
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `Balance Sheet${selectedDate && `-${selectedDate}`}${
          selectedArea && `-${selectedArea}`
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error("Failed to export balance.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { day: "numeric", month: "long", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-UK", options);
  };

  

  const handleNext = () => {
    if (!btnDisable) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      // fetchLedgers(nextPage);
    }
  };

  const handlePrev = () => {
    if (!btnDisable) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      // fetchLedgers(prevPage);
    }
  };


  return (
    <>
      <div className="whiteBgDiv UserManagementPadding">
        <div className="mainHeading">Customer Balance Sheet</div>
        <div className="formHeading UserManagementpage">
          Search Ledger Entries
        </div>
        <div
          className="d-flex flex-wrap flex-lg-nowrap"
          style={{ gap: "16px" }}
        >
          <div
            className="formInputLabelBox"
            style={{ flex: "1", minWidth: "250px" }}
          >
            <label>Choose Date</label>
            <input
              name="chooseDate"
              placeholder="Choose Date"
              type="date"
              id="date"
              lang="en-GB"
              value={selectedDate}
              onChange={handleDateChange}
            />
          </div>
          <div
            className="formInputLabelBox"
            style={{ flex: "1", minWidth: "250px" }}
          >
            <label>Choose Area</label>
            <select
              name="area"
              className="form-select"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              <option value="">Select area</option>
              {allAreas &&
                allAreas.map((area) => (
                  <option key={area._id} value={area.name}>
                    {area.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="UserManagementBtnFlexDiv">
          {!loading ? (
            <>
              <button className="colorBtn" onClick={filterLedgers}>
                Submit
              </button>
              <button className="grayBtn" onClick={printLedgers}>
                Print
              </button>
            </>
          ) : (
            <button className="grayBtn">Loading ...</button>
          )}
        </div>
      </div>

      <div className="whiteBgDiv dashboardTablePadding">
        <div className="mainHeading">Ledger Entries </div>
        <div
          className="align-items-center justify-content-start mt-3 tableEntriesCount"
          style={{ gap: "3px" }}
        >
          <div className="smallHeading tableEntriesCount">Show Entries</div>
          <select
            className="form-select"
            value={recordsPerPage}
            onChange={(e) => {
              setRecordsPerPage(e.target.value)
              setCurrentPage(1)
            }}
          >
            <option value={1}>1</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={150}>150</option>
            <option value={200}>200</option>
          </select>
        </div>
        <div className="tableDiv" style={{ paddingBottom: "69px" }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Date</th>
                <th scope="col">Customer</th>
                <th scope="col">Opening Balance</th>
                <th scope="col">Today’s Debit</th>
                <th scope="col">Today’s Credit</th>
                <th scope="col">Closing Balance</th>
              </tr>
            </thead>
            {!loading ? (
              <tbody>
                {ledgerEntries.length > 0 ? (
                  ledgerEntries.map((ledger, i) => {
                    const isLast = ledgerEntries.length - 1 <= i;
                    const roundUp = (value) =>
                      Number.isInteger(value) ? value : Math.ceil(value);
                    return (
                      <tr key={i}>
                        <th scope="row">
                          {i + (currentPage - 1) * recordsPerPage + 1}
                        </th>
                        <td>{formatDate(ledger.date)}</td>
                        <td>{ledger.customerName}</td>
                        <td>
                          {isLast ? (
                            <div
                              className="position-relative"
                              style={{ width: "max-content" }}
                            >
                              {roundUp(ledger.opening_Balance)}
                              <div className="totalvaluePositionAbsolute">
                                {totalOpeningBalance > 0 &&
                                  `Total: ${roundUp(totalOpeningBalance)}`}
                              </div>
                            </div>
                          ) : (
                            roundUp(ledger.opening_Balance)
                          )}
                        </td>
                        <td>
                          {isLast ? (
                            <div
                              className="position-relative"
                              style={{ width: "max-content" }}
                            >
                              {roundUp(ledger.todayDebit)}
                              <div className="totalvaluePositionAbsolute">
                                {totalTodaysDebit > 0 &&
                                  `Total: ${roundUp(totalTodaysDebit)}`}
                              </div>
                            </div>
                          ) : (
                            roundUp(ledger.todayDebit)
                          )}
                        </td>
                        <td>
                          {isLast ? (
                            <div
                              className="position-relative"
                              style={{ width: "max-content" }}
                            >
                              {roundUp(ledger.todayCredit)}
                              <div className="totalvaluePositionAbsolute">
                                {totalTodaysCredit > 0 &&
                                  `Total: ${roundUp(totalTodaysCredit)}`}
                              </div>
                            </div>
                          ) : (
                            roundUp(ledger.todayCredit)
                          )}
                        </td>
                        <td>
                          {isLast ? (
                            <div
                              className="position-relative"
                              style={{ width: "max-content" }}
                            >
                              {roundUp(ledger.closing_Balance)}
                              <div className="totalvaluePositionAbsolute">
                                {totalClosingBalance > 0 &&
                                  `Total: ${roundUp(totalClosingBalance)}`}
                              </div>
                            </div>
                          ) : (
                            roundUp(ledger.closing_Balance)
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <th scope="row" colSpan={6}>
                      No Entries Found
                    </th>
                  </tr>
                )}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <th scope="row" colSpan={6}>
                    Loading ...
                  </th>
                </tr>
              </tbody>
            )}
          </table>
          <div className="d-flex gap-1 justify-content-end">
            {totalPage > 1 && (
              <div className="tablePaginationBtnFlex">
                {currentPage > 1 && (
                  <button
                    className="grayBtn"
                    onClick={(e) => {
                      if (!btnDisable) {
                        handlePrev();
                      } else {
                        e.preventDefault();
                      }
                    }}
                    disabled={btnDisable}
                  >
                    Back
                  </button>
                )}
                {currentPage <= totalPage && currentPage !== totalPage && (
                  <button
                    className="colorBtn"
                    onClick={(e) => {
                      if (!btnDisable) {
                        handleNext();
                      } else {
                        e.preventDefault();
                      }
                    }}
                    disabled={btnDisable}
                  >
                    Next
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
export default BalanceSheet;
